import React from 'react';
import { Panel } from '../components/StatTile';

/**
 * On-call runbook (SSA-826): gamification alarm/signal → action mappings,
 * surfaced in-app next to the kill switch and economy dashboard so a responder
 * can go from symptom to action without leaving the tab.
 *
 * Alarm names below track the gamification monitoring stack; where a specific
 * CloudWatch alarm is not yet provisioned, the "signal" column is the economy
 * dashboard tile that surfaces the same condition.
 */

interface Row {
  signal: string;
  meaning: string;
  action: React.ReactNode;
}

const ROWS: Row[] = [
  {
    signal: 'Mint/burn ratio spikes (> ~3)',
    meaning: 'Coins are being minted far faster than spent — inflation / possible earning exploit.',
    action: (
      <>
        Check <b>Earn breakdown</b> for the dominant source. If one source dominates abnormally, consider disabling the
        related earning path via feature flag and flag suspected users.
      </>
    ),
  },
  {
    signal: 'Redemption success rate drops',
    meaning: 'Confirmed redemptions falling vs expirations — venue-side friction or a redemption bug.',
    action: (
      <>
        Cross-check <b>Top venues by expiry rate</b>. A single venue spiking → venue/staff issue. Broad drop → suspect a
        redemption regression; page the on-call engineer.
      </>
    ),
  },
  {
    signal: 'Spin distribution deviates from configured weights',
    meaning: 'Actual payout percentages diverge from the AppConfig weights — misconfig or spin exploit.',
    action: (
      <>
        Use the <b>Spin-wheel kill switch</b> to disable spin immediately, then reconcile the{' '}
        <code>gamification_spin_weights</code> AppConfig value before re-enabling.
      </>
    ),
  },
  {
    signal: 'Suspicious-users count climbs',
    meaning: 'The fraud heuristics are flagging more users; the grouped reason shows which heuristic fired.',
    action: (
      <>
        Review flagged users in <b>User Lookup</b>. Confirmed fraud → keep flagged (earning already blocked). False
        positives → unflag with a reason.
      </>
    ),
  },
  {
    signal: 'Outstanding liability jumps sharply',
    meaning: 'Total unredeemed coin balance rising fast — financial exposure.',
    action: (
      <>
        Correlate with mint/burn. If driven by a single earning source or user cohort, throttle the source and adjust
        affected wallets via <b>Adjust coins</b>.
      </>
    ),
  },
  {
    signal: 'Leaderboard looks stale after a fraud cleanup',
    meaning: 'Snapshots reflect pre-cleanup data.',
    action: (
      <>
        Use <b>Re-run leaderboard snapshot</b> to recompute now (recomputes all active markets).
      </>
    ),
  },
];

export function RunbookPanel() {
  return (
    <div className="space-y-4">
      <Panel title="On-call runbook — gamification" subtitle="Symptom → action. Keep this open during an incident.">
        <div className="space-y-4">
          {ROWS.map((r) => (
            <div key={r.signal} className="border-l-2 border-violet-700/60 pl-4">
              <p className="text-sm font-semibold text-white">{r.signal}</p>
              <p className="text-xs text-gray-500 mt-0.5">{r.meaning}</p>
              <p className="text-sm text-gray-300 mt-1.5">{r.action}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Kill-switch procedure" subtitle="When in doubt, stop the bleeding first.">
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-300">
          <li>Open the <b>Kill Switch</b> panel and confirm the environment (prod vs dev) in the selector.</li>
          <li>Disable spin with a short reason — the flag flips in AppConfig and takes effect within ~1 minute.</li>
          <li>Verify on the <b>Economy health</b> dashboard that spin activity drops.</li>
          <li>Root-cause, fix config/code, then re-enable spin with a reason. Every toggle is audit-logged.</li>
        </ol>
      </Panel>

      <Panel title="Escalation" subtitle="If the tools here don't resolve it.">
        <p className="text-sm text-gray-300">
          Page the on-call engineer for anything that looks like a code regression (broad redemption failures, errors in
          logs) rather than a config/fraud issue. Include the affected environment, the dashboard window, and the
          specific tile that surfaced the problem.
        </p>
      </Panel>
    </div>
  );
}
